import { ApiClient } from "@/utils/apiClient";
import type { ApiResponse } from "@/models/api";
import type { CardApiDto, CardSystem } from "@/models/card";
import { getUTMParamsAsHeaders } from "../utils/utm";

export type CardHostedFormGetResponse = ApiResponse<{
  scriptContent: string;
}>;

export type CardsGetPayload = {
  withDeleted?: boolean;
}
export type CardsGetResponse = ApiResponse<{
  items: CardApiDto[];
}>;

export type CardPostPayload = {
  name: string;
  system: CardSystem;
  currencyCode: string;
  platformData: Object;
  membershipTierId?: number;
  membershipTierPlanId?: number;
  destinationId?: number;
  maxRoundUp?: number;
  billing?: boolean;
};
export type CardPostResponse = ApiResponse<{
  card: CardApiDto;
}>;

export type CardResumeResponse = ApiResponse<void>;
export type CardPauseResponse = ApiResponse<void>;

export abstract class CardApiService {
  public static async getCardHostedForm(): Promise<CardHostedFormGetResponse> {
    const response = await ApiClient.getClient().get<CardHostedFormGetResponse>('/card-hosted-form');
    return response.data;
  }

  public static async list(payload: CardsGetPayload): Promise<CardsGetResponse> {
    const response = await ApiClient.getClient().get<CardsGetResponse>(
      '/cards',
      {
        params: {
          ...payload,
          withPlatform: 1,
        },
      },
    );
    return response.data;
  }

  public static async create(payload: CardPostPayload): Promise<CardPostResponse> {
    const response = await ApiClient.getClient().post<CardPostResponse>(
      '/cards',
      payload,
      {
        headers: {
          ...getUTMParamsAsHeaders(),
        },
      },
    );
    return response.data;
  }

  public static async resume(id: number): Promise<CardResumeResponse> {
    const response = await ApiClient.getClient().post<CardResumeResponse>(`/cards/${id}/resume`);
    return response.data;
  }

  public static async pause(id: number, force: boolean = false): Promise<CardPauseResponse> {
    const response = await ApiClient.getClient().post<CardPauseResponse>(`/cards/${id}/pause`);
    return response.data;
  }

  public static async delete(id: number, force: boolean = false): Promise<void> {
    await ApiClient.getClient().delete(`/cards/${id}`);
    // No response data
  }

  public static async setAsDefault(id: number): Promise<void> {
    await ApiClient.getClient().post(`/cards/${id}/set-as-default`);
    // No response data
  }
}
